// Discovery for the page world: one bounded walk over the page finds what companions can stand on
// (text, headings, boxes, images, buttons, search fields) and what they must not cover. The
// rectangles read here only decide what counts; page-world.js re-reads them every frame.
//
// Owners are the engine's "windows". Text belongs to a group (a card, an article, a nav, a scroll
// box, the page), so a companion can hop between the lines and headings of one group; boxes,
// images, controls and search fields own themselves. z follows the page: higher up is above, a
// child is above its parent, sticky and fixed bars are above everything.

const SKIP = new Set(['script', 'style', 'noscript', 'template', 'head', 'link', 'meta', 'title', 'br', 'wbr',
  'option', 'optgroup', 'datalist', 'map', 'area', 'track', 'source', 'param']);
const MEDIA = new Set(['img', 'svg', 'video', 'canvas', 'iframe', 'object', 'embed']);
const GROUPS = new Set(['article', 'aside', 'nav', 'dialog']);
const NAV_ROLES = new Set(['navigation', 'toolbar', 'menubar', 'tablist']);
const EDITOR = '.CodeMirror, .cm-editor, .monaco-editor, .ace_editor, [role=textbox], [role=combobox], [role=slider], [role=spinbutton]';
const BUTTON_INPUTS = new Set(['button', 'submit', 'reset', 'image']);
const TEXT_INPUTS = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number', '']);
const INLINE = /^(inline|ruby)/;   // display values laid out on the parent's lines

// ---------- colors: is this box visible against what is behind it? ----------
function parseColor(text) {
  const m = /^rgba?\(([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\)$/.exec(text);
  if (m) return [+m[1], +m[2], +m[3], m[4] == null ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : +m[4]];
  if (text === 'transparent') return [0, 0, 0, 0];
  return null;                                        // a color space we don't parse (oklch...): treated as visible
}
const over = (top, under) => {
  const a = top[3];
  return [0, 1, 2].map(i => top[i] * a + under[i] * (1 - a)).concat(1);
};
const differs = (a, b) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]) >= 18;

// The background of the page itself: the root's, else the body's (CSS propagates it), else white.
function pageBackground(doc, win) {
  for (const el of [doc.documentElement, doc.body]) {
    if (!el) continue;
    const c = parseColor(win.getComputedStyle(el).backgroundColor);
    if (c && c[3] > 0.5) return over(c, [255, 255, 255, 1]);
  }
  return [255, 255, 255, 1];
}

// Visible background, a top border or a shadow: it reads as a box. Returns what is behind its
// children, and whether the box itself shows.
function boxLook(cs, behind) {
  const bg = parseColor(cs.backgroundColor);
  let shows = cs.backgroundImage !== 'none' || cs.boxShadow !== 'none', next = behind;
  if (!bg) shows = true;
  else if (bg[3] > 0.04) { next = over(bg, behind); if (bg[3] >= 0.12 && differs(next, behind)) shows = true; }
  if (!shows && parseFloat(cs.borderTopWidth) >= 1 && cs.borderTopStyle !== 'none' && cs.borderTopStyle !== 'hidden') {
    const c = parseColor(cs.borderTopColor);
    shows = !c || (c[3] > 0.15 && differs(over(c, next), next));
  }
  // A bar that hides what scrolls under it, even when it has the page's own color.
  const opaque = !bg || bg[3] >= 0.5 || cs.backgroundImage !== 'none' || (cs.backdropFilter || 'none') !== 'none';
  return { shows, opaque, behind: next };
}

// Paints nothing: no background, border or shadow, no text of its own, no ::before or ::after that
// shows (a link stretched over a whole tile, an empty click catcher).
const clear = cs => {
  const bg = parseColor(cs.backgroundColor);
  if (!bg || bg[3] >= 0.04 || cs.backgroundImage !== 'none' || cs.boxShadow !== 'none' || (cs.backdropFilter || 'none') !== 'none') return false;
  return ['Top', 'Right', 'Bottom', 'Left'].every(side => !(parseFloat(cs[`border${side}Width`]) > 0)
    || cs[`border${side}Style`] === 'none' || cs[`border${side}Style`] === 'hidden' || (parseColor(cs[`border${side}Color`])?.[3] ?? 1) < 0.04);
};
export function seeThrough(el, win) {
  if (MEDIA.has(el.localName) || isControl(el) || isEditor(el)) return false;
  const cs = win.getComputedStyle(el);
  if (parseFloat(cs.opacity) < 0.05) return true;
  if (!clear(cs)) return false;
  for (const node of el.childNodes) if (node.nodeType === 3 && /\S/.test(node.data)) return false;
  for (const pseudo of ['::before', '::after']) {
    const p = win.getComputedStyle(el, pseudo);
    if (p.content !== 'none' && p.content !== 'normal' && (p.content !== '""' || !clear(p))) return false;
  }
  return true;
}

// ---------- what an element is ----------
function isEditor(el) {
  return el.isContentEditable || el.localName === 'textarea' || el.matches(EDITOR);
}
function isSearch(el, inSearch) {
  if (el.getAttribute('role') === 'searchbox') return true;
  if (el.localName !== 'input') return false;
  const type = (el.getAttribute('type') || '').toLowerCase();
  return type === 'search' || (inSearch && TEXT_INPUTS.has(type));
}
function isControl(el) {
  const name = el.localName;
  if (name === 'button' || name === 'select' || el.getAttribute('role') === 'button') return true;
  return name === 'input';
}
const buttonLike = el => el.localName === 'button' || el.localName === 'select' || el.getAttribute('role') === 'button'
  || (el.localName === 'input' && BUTTON_INPUTS.has((el.getAttribute('type') || '').toLowerCase()));
const isHeading = el => /^h[1-6]$/.test(el.localName) || el.getAttribute('role') === 'heading';
// A link styled as a button: its text is its label, not a line to sit at the end of.
const buttonish = (el, cs, look, rect) => el.localName === 'a' && rect.height >= 18 && rect.height <= 64
  && (look.shows || /^inline-(block|flex|grid)$/.test(cs.display)) && !el.querySelector('h1,h2,h3,h4,h5,h6,p,li,img,picture,video');

// The text that belongs to this block's own lines: its text nodes and plain inline elements,
// not atomic inline boxes (badges, buttons) or controls.
function ownText(node, win) {
  if (node.nodeType === 3) return /\S/.test(node.data);
  if (node.nodeType !== 1 || SKIP.has(node.localName) || isControl(node) || isEditor(node)) return false;
  if (node.getAttribute('aria-hidden') === 'true') return false;
  const cs = win.getComputedStyle(node);
  if (cs.display !== 'inline' || cs.visibility !== 'visible') return false;
  for (const child of node.childNodes) if (ownText(child, win)) return true;
  return false;
}

// Children as the page renders them: an open shadow root replaces the light children, a slot
// shows what is assigned to it.
function renderedChildren(el) {
  if (el.shadowRoot) return [...el.shadowRoot.children];
  if (el.localName === 'slot') {
    const assigned = el.assignedElements({ flatten: true });
    return assigned.length ? assigned : [...el.children];
  }
  return [...el.children];
}

// ---------- the walk ----------
// band: { top, bottom } in client coordinates; whole subtrees outside it are skipped.
export function scanPage({ root, doc, win, exclude, idOf, band, budget = 5000, view }) {
  const owners = [], blocks = [], items = [], shadowRoots = new Set();
  let visited = 0, truncated = false;
  const body = root.nodeType === 9 ? (doc.body || doc.documentElement) : root;
  const vw = view.width, vh = view.height;

  function addItem(rec) { rec.index = items.length; items.push(rec); return rec; }
  function addOwner(el, type, c, rect, extra = {}) {
    const fixedLike = c.layer > 0 && c.layerRoot === el;
    const o = addItem({ el, id: idOf(el), type, parent: fixedLike ? null : c.owner, layer: c.layer, rect,
      ctx: c.ctx, clips: c.clips, anc: c.anc, frame: c.frame, scope: c.frame, children: [], z: 0, near: true, edge: null, covered: false,
      fixed: c.layer > 0, ...extra });
    (o.parent ? o.parent.children : owners.roots).push(o);
    owners.push(o);
    return o;
  }
  owners.roots = [];
  const childAnc = (c, o) => ({ ...c, anc: new Set(c.anc).add(o.index) });
  const outside = node => { for (const host of exclude) if (host === node || host.contains(node)) return false; return true; };
  // A fixed layer drawn behind the page (a negative z-index), or a big one the pointer never reaches
  // and that nothing on screen shows on top: a backdrop, not a bar companions must keep out of.
  function behind(el, cs, rect) {
    if (parseFloat(cs.zIndex) < 0) return true;
    if (rect.width * rect.height < 0.3 * vw * vh) return false;
    for (const [fx, fy] of [[0.5, 0.5], [0.25, 0.3], [0.75, 0.7]]) {
      const x = Math.min(vw - 1, Math.max(0, rect.left + fx * rect.width)), y = Math.min(vh - 1, Math.max(0, rect.top + fy * rect.height));
      const top = doc.elementsFromPoint(x, y).find(outside);
      if (top && (top === el || el.contains(top))) return false;
    }
    return true;
  }

  function visit(el, cs, c) {
    if (++visited > budget) { truncated = true; return; }
    const name = el.localName;
    const rect = el.getBoundingClientRect();
    const pos = cs.position, fixedHere = pos === 'fixed', stickyHere = pos === 'sticky';
    if (!fixedHere && !stickyHere && c.layer === 0 && rect.height > 0 && (rect.bottom < band.top || rect.top > band.bottom)) return;
    if ((fixedHere || stickyHere) && c.layer === 0 && behind(el, cs, rect)) return;   // a backdrop or decoration, not a bar
    if (fixedHere || stickyHere) {
      c = { ...c, layer: fixedHere ? 2 : Math.max(c.layer, 1), layerRoot: el, ctx: el,
        clips: fixedHere ? [] : c.clips };
    }
    const shown = cs.visibility === 'visible';
    const big = rect.width >= 48 && rect.height >= 20;

    // Leaves: editors and text fields, search fields, controls, media. Nothing inside them counts.
    if (isEditor(el) || (name === 'input' && !isSearch(el, c.inSearch) && !buttonLike(el))) {
      if (shown && rect.width > 2 && rect.height > 2) addOwner(el, 'control', c, rect);
      return;
    }
    if (isSearch(el, c.inSearch)) {
      if (shown && big) addOwner(el, 'search', c, rect, { edge: { weight: 4 }, dir: cs.direction,
        radius: radiusOf(cs, rect), input: name === 'input' ? el : null });
      return;
    }
    if (isControl(el)) {
      if (shown && rect.width > 2 && rect.height > 2) {
        addOwner(el, 'control', c, rect, { edge: rect.width >= 56 && rect.height >= 24 ? { weight: 1.2 } : null, radius: radiusOf(cs, rect) });
      }
      return;
    }
    if (MEDIA.has(name)) {
      if (shown && rect.width >= 24 && rect.height >= 16) {
        addOwner(el, 'media', c, rect, { edge: rect.width >= 48 && rect.height >= 24 ? { weight: 1.6 } : null, radius: radiusOf(cs, rect) });
      }
      return;
    }

    const look = boxLook(cs, c.behind);
    if (shown && buttonish(el, cs, look, rect)) {
      addOwner(el, 'control', c, rect, { edge: rect.width >= 56 && rect.height >= 24 ? { weight: 1.2 } : null, radius: radiusOf(cs, rect) });
      return;
    }
    let next = { ...c, behind: look.behind };
    if (el.getAttribute('role') === 'search' || name === 'search') next.inSearch = true;
    const clips = cs.overflowX !== 'visible' || cs.overflowY !== 'visible' || /paint|strict|content/.test(cs.contain);
    if (clips && el !== body && el !== doc.documentElement) {
      if (rect.width <= 2 || rect.height <= 2) return;                  // visually hidden text (sr-only)
      // Its inside is cut to its box; only a box that scrolls moves its inside on its own.
      next = { ...next, clips: [...next.clips, el], ctx: /auto|scroll|overlay/.test(cs.overflowX + cs.overflowY) ? el : next.ctx };
    }

    // A box, a hint, a bar, a scroll box or a sectioning element gets an owner of its own.
    const hint = el.hasAttribute('data-companions-surface');
    const box = shown && (hint || (look.shows && big && el !== body) || (c.layerRoot === el && look.opaque && big));
    const role = el.getAttribute('role');
    const group = el === body || GROUPS.has(name) || NAV_ROLES.has(role) || role === 'dialog'
      || c.layerRoot === el || (clips && el !== doc.documentElement);
    let owner = null;
    if (box) {
      const area = rect.width * rect.height, background = !hint && c.layer === 0 && (rect.height > 0.6 * vh || area > 0.4 * vw * vh);
      owner = addOwner(el, 'box', c, rect, { background, edge: { weight: hint ? 3 : background ? 1 : 2 }, radius: radiusOf(cs, rect) });
    } else if (group) {
      owner = addOwner(el, 'group', c, rect);
    }
    if (owner) {
      next = { ...next, owner, group: owner };
      if (owner.type !== 'group') next = childAnc(next, owner);
      if (owner.type !== 'group' && !owner.background) next.frame = owner;   // what is inside it stays inside it
    }

    if (name === 'pre') next.code = true;                              // code is read line by line: no seats in it
    // Text: a block with lines of its own.
    if (shown && !next.code && !INLINE.test(cs.display) && cs.display !== 'contents') {
      const runs = textRuns(el);
      if (runs.length) addBlock(el, cs, next, rect, runs, false);
    }
    // A nav bar or toolbar without a box: companions stand on its row of links.
    if (shown && !box && (name === 'nav' || NAV_ROLES.has(role)) && rect.width >= 80 && rect.height <= 160) {
      addBlock(el, cs, next, rect, [[null, null]], true);
    }

    if (el.shadowRoot && !shadowRoots.has(el.shadowRoot)) shadowRoots.add(el.shadowRoot);
    for (const child of renderedChildren(el)) {
      if (SKIP.has(child.localName) || exclude.has(child) || child.hasAttribute('data-companions-ignore')) continue;
      if (child.getAttribute('aria-hidden') === 'true') continue;
      const ccs = win.getComputedStyle(child);
      if (ccs.display === 'none' || parseFloat(ccs.opacity) < 0.05 || ccs.contentVisibility === 'hidden') continue;
      visit(child, ccs, next);
      if (truncated) return;
    }
  }

  // Runs of the block's own inline children, split by block children: [first, last] node pairs.
  function textRuns(el) {
    const runs = [];
    let first = null, last = null, text = false;
    const close = () => { if (first && text) runs.push([first, last]); first = last = null; text = false; };
    for (const node of el.childNodes) {
      if (node.nodeType === 1) {
        if (SKIP.has(node.localName)) continue;
        const display = win.getComputedStyle(node).display;
        if (display === 'none') continue;
        if (!INLINE.test(display) && display !== 'contents') { close(); continue; }
      } else if (node.nodeType !== 3) continue;
      first ??= node; last = node;
      if (!text && ownText(node, win)) text = true;
    }
    close();
    return runs;
  }

  function addBlock(el, cs, c, rect, runs, nav) {
    const fs = parseFloat(cs.fontSize) || 16;
    const heading = nav || isHeading(el) || (fs >= 22 && rect.height < fs * 3.2);
    blocks.push(addItem({ el, id: idOf(el), owner: c.group, runs, nav, heading, ends: !nav, fs,
      scale: Math.max(2, Math.min(8, Math.round(fs / 4.6))), dir: cs.direction, rect,
      ctx: c.ctx, clips: c.clips, anc: c.anc, frame: c.frame, scope: c.frame, layer: c.layer, near: true, covered: false, lines: null }));
  }

  const start = { owner: null, group: null, frame: null, layer: 0, layerRoot: null, ctx: null, clips: [], anc: new Set(),
    behind: pageBackground(doc, win), inSearch: false };
  const rootCs = win.getComputedStyle(body);
  if (rootCs.display !== 'none') visit(body, rootCs, start);
  overlays(owners, items);
  stack(owners);
  return { owners, blocks, items, shadowRoots, truncated, visited };
}

// Rounded corners: companions keep off the curve.
function radiusOf(cs, rect) {
  const r = cs.borderTopLeftRadius, v = parseFloat(r) || 0;
  return Math.min(r.endsWith('%') ? (v / 100) * rect.width : v, rect.height / 2);
}

// Text, boxes and buttons lying on an image (a hero title over a photo) are drawn over it: the
// image counts as their parent, so it neither hides them nor cuts their spots.
function overlays(owners, items) {
  const area = o => o.rect.width * o.rect.height;
  const media = owners.filter(o => o.type === 'media' && o.layer === 0 && area(o) > 2000);
  if (!media.length) return;
  const inside = (a, b) => a.left >= b.left - 2 && a.right <= b.right + 2 && a.top >= b.top - 2 && a.bottom <= b.bottom + 2;
  const hostFor = rect => media.reduce((best, m) => (inside(rect, m.rect) && (!best || area(m) < area(best)) ? m : best), null);
  const groups = new Map();                                            // one text group per owner and image
  for (const item of items) {
    if (item.layer !== 0 || item.type === 'media' || item.type === 'group') continue;
    const host = hostFor(item.rect);
    if (!host) continue;
    item.anc = new Set(item.anc).add(host.index);
    item.frame ??= host;
    const parent = item.runs ? item.owner : item.parent;
    if (parent && parent !== host && parent.type !== 'group' && inside(parent.rect, host.rect)) continue;   // its box lies on the image
    if (item.runs) {
      const key = `${item.owner?.id}|${host.id}`;
      let group = groups.get(key);
      if (!group) {
        group = { el: item.el, id: `${item.owner?.id ?? item.id}:on${host.id}`, type: 'group', parent: host, layer: 0, rect: item.rect,
          ctx: item.ctx, clips: item.clips, anc: item.anc, children: [], z: 0, near: true, edge: null, covered: false, fixed: false, index: -1 };
        groups.set(key, group); host.children.push(group); owners.push(group);
      }
      item.owner = group;
    } else if (item.parent !== host) {
      const from = item.parent ? item.parent.children : owners.roots;
      from.splice(from.indexOf(item), 1);
      item.parent = host; host.children.push(item);
    }
  }
}

// z: a parent, then its children (each above the parent), then its next sibling up the page.
// Lower on the page comes first (below); sticky, then fixed, last (above everything).
function stack(owners) {
  let z = 1;
  const order = list => list.sort((a, b) => (a.layer - b.layer) || (b.rect.top - a.rect.top) || (b.rect.left - a.rect.left));
  const assign = o => { o.z = o.layer === 2 ? 1e6 + z++ : o.layer === 1 ? 5e5 + z++ : z++; for (const c of order(o.children)) assign(c); };
  for (const o of order(owners.roots)) assign(o);
}
